import { Collaborator } from 'jobs/Job'
import { computed, makeObservable } from 'mobx'
import JobViewModel from '../JobViewModel'
import { task } from 'mobx-task'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { GetCurrentMember } from 'iam/SessionStore'

export class CollaboratorViewModel {
  constructor(
    private jobVM: JobViewModel,
    private newAlert: AlertFactory,
    public collaborator: Collaborator,
    private getCurrentMember: GetCurrentMember,
  ) {
    makeObservable(this)
  }

  @computed
  get member() {
    return this.collaborator.member
  }

  @computed
  get type() {
    return this.collaborator.type
  }

  @computed
  get formattedType() {
    return this.collaborator.type === 'PROVIDER' ? 'Pro' : 'Client'
  }

  @computed
  get role() {
    return this.collaborator.role
  }

  @computed
  get formattedRole() {
    return this.collaborator.role === 'CHAMPION' ? 'Champion' : 'Supporting'
  }

  @computed
  get team() {
    // Make sure we can actually find the owner team on the job.
    if (this.type === 'CLIENT' && this.role === 'CHAMPION') {
      return this.collaborator.job.ownerTeam?.id
        ? this.collaborator.job.teams.find(
            (x) => x.id === this.collaborator.job.ownerTeam?.id,
          )
        : null
    }
    return this.collaborator.team
  }

  @computed
  get isCurrentUser() {
    return this.collaborator.member.isCurrentUser
  }

  @computed
  get canViewCollaboratorInfo() {
    return Boolean(this.canViewProInfo || this.canViewClientInfo || this.team)
  }

  @computed
  get canViewCollaboratorEmail() {
    return Boolean(
      (this.canViewProInfo || this.canViewClientInfo) && this.member.email,
    )
  }

  @computed
  get canViewCollaboratorPhoneNumber() {
    return Boolean(
      (this.canViewProInfo || this.canViewClientInfo) && this.member.phone,
    )
  }

  @computed
  get canViewClientInfo() {
    return (
      this.getCurrentMember(this.collaborator.job.workspace.id).hasPermission(
        'VIEW_CLIENT_DETAILS',
      ) && this.collaborator.type === 'CLIENT'
    )
  }

  @computed
  get canViewProInfo() {
    return this.getCurrentMember(
      this.collaborator.job.workspace.id,
    ).hasPermission('ADMIN_PRO_PI_VIEW')
  }

  /**
   * Indicates whether the actor can view this collaborator in HQ.
   */
  @computed
  get canViewUserDetails() {
    return this.getCurrentMember(
      this.collaborator.job.workspace.id,
    ).hasPermission('ADMIN_ACCESS')
  }

  activate() {
    //
  }

  deactivate() {
    //
  }

  remove = task.resolved(async () => {
    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Removing Collaborator',
        `Removing ${this.member.displayName} from ${this.jobVM.job.name}...`,
      )
      .show()
    try {
      await this.jobVM.removeCollaborator(this)
      alert
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `${this.member.displayName} was removed from ${this.jobVM.job.name}.`,
        )
        .autoDismiss()
    } catch (error) {
      alert.fromError(error)
      throw error
    }
  })
}
